import { Badge, makeStyles, tokens } from '@coreai/fluentui-react';

export type DotStatusKind = 'Active' | 'Inactive' | 'Error' | 'Provisioning' | 'Deleting';

interface DotStatusConfig {
  color: 'success' | 'danger' | 'severe' | 'informative';
  label: string;
}

// `severe` (dark orange) is used instead of `warning` (yellow) for in-progress
// states. With `appearance="ghost"` the text sits directly on the page
// background, and Fluent's yellow foreground token fails WCAG 1.4.3 (~3.9:1
// on white). Dark orange measures ~6:1 and still reads as a warning.
const STATUS_CONFIG: Record<DotStatusKind, DotStatusConfig> = {
  Active: { color: 'success', label: 'Active' },
  Inactive: { color: 'informative', label: 'Inactive' },
  Error: { color: 'danger', label: 'Error' },
  Provisioning: { color: 'severe', label: 'Provisioning' },
  Deleting: { color: 'severe', label: 'Deleting' },
};

const useStyles = makeStyles({
  badge: {
    fontWeight: tokens.fontWeightSemibold,
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalS,
    columnGap: tokens.spacingHorizontalXS,
  },
  dot: {
    display: 'inline-block',
    width: '8px',
    height: '8px',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: 'currentColor',
  },
});

export interface DotStatusBadgeProps {
  status: DotStatusKind;
}

/**
 * Ghost status pill with a leading colored dot — e.g. `● Active` (green) or
 * `● Error` (red). Used by the Apps, Workflows, and Dashboard tables. The
 * `ghost` appearance (no fill, no border) is deliberate: it keeps data-dense
 * tables visually quiet so the status colour and label do the talking.
 *
 * For non-data-table status displays, prefer `StatusBadge` in
 * `components/shared/StatusBadge.tsx` (filled appearance, slightly different
 * vocabulary including `Ready` / `Failed`).
 */
export const DotStatusBadge = ({ status }: DotStatusBadgeProps) => {
  const styles = useStyles();
  const { color, label } = STATUS_CONFIG[status];

  return (
    <Badge
      appearance="ghost"
      color={color}
      shape="rounded"
      size="medium"
      icon={<span className={styles.dot} aria-hidden="true" />}
      className={styles.badge}
      aria-label={`Status: ${label}`}
    >
      {label}
    </Badge>
  );
};
